import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  message,
  Steps,
  Row,
  Col,
  Descriptions,
  Tag,
  Progress,
  Alert,
  Checkbox,
  InputNumber,
  Tabs,
} from 'antd';
import {
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  UploadOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_GOODS_RECEIPT_NOTE } from '@/constants/document-entity-types';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Dragger } = Upload;

interface Verification {
  id: string;
  verification_number: string;
  grn_id?: string;
  grn_number: string;
  supplier_name: string;
  verification_date: string;
  status: string;
  overall_result?: string;
  has_non_conformance: boolean;
}

interface ChecklistItem {
  id?: string;
  item_category: string;
  item_description: string;
  is_mandatory: boolean;
  check_result: 'Pass' | 'Fail' | 'N/A' | 'Pending';
  actual_finding?: string;
  deviation?: string;
  comments?: string;
}

interface MTCRecord {
  mtc_number: string;
  certificate_type: string;
  issuing_mill?: string;
  issue_date: Date;
  heat_number?: string;
  batch_number?: string;
  material_grade?: string;
  material_specification?: string;
  chemical_composition?: any;
  tensile_strength?: number;
  yield_strength?: number;
  elongation?: number;
  document_url: string;
}

const ReceivingVerificationPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [selectedVerification, setSelectedVerification] = useState<any>(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [certificates, setCertificates] = useState<any[]>([]);
  const [mtcRecords, setMTCRecords] = useState<MTCRecord[]>([]);
  
  const [createForm] = Form.useForm();
  const [checklistForm] = Form.useForm();
  const [certificateForm] = Form.useForm();
  const [mtcForm] = Form.useForm();

  useEffect(() => {
    fetchVerifications();
  }, []);

  const fetchVerifications = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/procurement/receiving-verifications');
      setVerifications(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch verifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationDetails = async (id: string) => {
    try {
      const response = await apiClient.get(`/procurement/receiving-verifications/${id}`);
      setSelectedVerification(response.data.data);
      setChecklistItems(response.data.data.checklist_items || []);
      setCertificates(response.data.data.certificates || []);
      setMTCRecords(response.data.data.mtc_records || []);
      setDetailsModalVisible(true);
    } catch (error) {
      message.error('Failed to fetch verification details');
    }
  };

  const handleCreateVerification = async (values: any) => {
    try {
      await apiClient.post('/procurement/receiving-verifications', {
        grn_id: values.grn_id,
        po_id: values.po_id,
        supplier_id: values.supplier_id,
        verification_date: values.verification_date.format('YYYY-MM-DD'),
        verification_type: values.verification_type,
      });
      message.success('Verification created successfully');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchVerifications();
    } catch (error) {
      message.error('Failed to create verification');
    }
  };

  const handleSubmitChecklistItem = async (values: any) => {
    if (!selectedVerification) return;

    try {
      await apiClient.post(
        `/procurement/receiving-verifications/${selectedVerification.id}/checklist`,
        {
          item_category: values.item_category,
          item_description: values.item_description,
          is_mandatory: values.is_mandatory,
          check_result: values.check_result,
          actual_finding: values.actual_finding,
          deviation: values.deviation,
          comments: values.comments,
        }
      );
      message.success('Checklist item submitted');
      checklistForm.resetFields();
      fetchVerificationDetails(selectedVerification.id);
    } catch (error) {
      message.error('Failed to submit checklist item');
    }
  };

  const handleSubmitCertificate = async (values: any) => {
    if (!selectedVerification) return;

    try {
      await apiClient.post(
        `/procurement/receiving-verifications/${selectedVerification.id}/certificates`,
        {
          certificate_type: values.certificate_type,
          certificate_number: values.certificate_number,
          issuing_authority: values.issuing_authority,
          issue_date: values.issue_date?.format('YYYY-MM-DD'),
          document_url: values.document_url,
          material_heat_number: values.material_heat_number,
          material_batch_number: values.material_batch_number,
        }
      );
      message.success('Certificate submitted successfully');
      certificateForm.resetFields();
      fetchVerificationDetails(selectedVerification.id);
    } catch (error) {
      message.error('Failed to submit certificate');
    }
  };

  const handleSubmitMTC = async (values: any) => {
    if (!selectedVerification) return;

    try {
      await apiClient.post('/procurement/mtc-records', {
        verification_id: selectedVerification.id,
        grn_id: selectedVerification.grn_id,
        item_id: values.item_id,
        supplier_id: selectedVerification.supplier_id,
        mtc_number: values.mtc_number,
        certificate_type: values.certificate_type,
        issuing_mill: values.issuing_mill,
        issue_date: values.issue_date.format('YYYY-MM-DD'),
        heat_number: values.heat_number,
        batch_number: values.batch_number,
        material_grade: values.material_grade,
        material_specification: values.material_specification,
        chemical_composition: values.chemical_composition,
        tensile_strength: values.tensile_strength,
        yield_strength: values.yield_strength,
        elongation: values.elongation,
        mechanical_properties: values.mechanical_properties,
        document_url: values.document_url,
      });
      message.success('MTC record created successfully');
      mtcForm.resetFields();
      fetchVerificationDetails(selectedVerification.id);
    } catch (error) {
      message.error('Failed to create MTC record');
    }
  };

  const handleCompleteVerification = async () => {
    if (!selectedVerification) return;

    try {
      const response = await apiClient.post(
        `/procurement/receiving-verifications/${selectedVerification.id}/complete`,
        {
          notes: 'Verification completed',
        }
      );
      
      const result = response.data.data;
      
      if (result.overall_result === 'Accepted') {
        message.success('Verification completed successfully - All checks passed');
      } else if (result.overall_result === 'Rejected') {
        message.warning('Verification completed - Non-conformances detected. NCR created.');
      } else {
        message.info('Verification completed - Conditional acceptance');
      }
      
      setDetailsModalVisible(false);
      fetchVerifications();
    } catch (error) {
      message.error('Failed to complete verification');
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig: { [key: string]: { color: string; icon: React.ReactNode } } = {
      Pending: { color: 'orange', icon: <WarningOutlined /> },
      Completed: { color: 'green', icon: <CheckCircleOutlined /> },
      Rejected: { color: 'red', icon: <CloseCircleOutlined /> },
    };
    const config = statusConfig[status] || { color: 'default', icon: null };
    return (
      <Tag color={config.color} icon={config.icon}>
        {status}
      </Tag>
    );
  };

  const getResultTag = (result: string) => {
    const resultConfig: { [key: string]: { color: string } } = {
      Accepted: { color: 'green' },
      Rejected: { color: 'red' },
      Conditional: { color: 'orange' },
    };
    const config = resultConfig[result] || { color: 'default' };
    return <Tag color={config.color}>{result}</Tag>;
  };

  const getCheckResultIcon = (result: string) => {
    switch (result) {
      case 'Pass':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />;
      case 'Fail':
        return <CloseCircleOutlined style={{ color: '#f5222d', fontSize: 18 }} />;
      case 'N/A':
        return <span style={{ color: '#8c8c8c' }}>N/A</span>;
      case 'Pending':
        return <WarningOutlined style={{ color: '#faad14', fontSize: 18 }} />;
      default:
        return null;
    }
  };

  const columns: ColumnsType<Verification> = [
    {
      title: 'Verification No.',
      dataIndex: 'verification_number',
      key: 'verification_number',
      width: 150,
    },
    {
      title: 'GRN Number',
      dataIndex: 'grn_number',
      key: 'grn_number',
      width: 150,
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 200,
    },
    {
      title: 'Verification Date',
      dataIndex: 'verification_date',
      key: 'verification_date',
      width: 120,
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Result',
      dataIndex: 'overall_result',
      key: 'overall_result',
      width: 120,
      render: (result) => (result ? getResultTag(result) : '-'),
    },
    {
      title: 'NCR',
      dataIndex: 'has_non_conformance',
      key: 'has_non_conformance',
      width: 80,
      render: (hasNCR) => (hasNCR ? <Tag color="red">Yes</Tag> : <Tag color="green">No</Tag>),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => fetchVerificationDetails(record.id)}
          >
            View Details
          </Button>
        </Space>
      ),
    },
  ];

  const checklistColumns: ColumnsType<ChecklistItem> = [
    {
      title: 'Category',
      dataIndex: 'item_category',
      key: 'item_category',
      width: 120,
      render: (category) => <Tag>{category}</Tag>,
    },
    {
      title: 'Check Item',
      dataIndex: 'item_description',
      key: 'item_description',
      width: 300,
    },
    {
      title: 'Mandatory',
      dataIndex: 'is_mandatory',
      key: 'is_mandatory',
      width: 100,
      render: (mandatory) => (mandatory ? <Tag color="red">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: 'Result',
      dataIndex: 'check_result',
      key: 'check_result',
      width: 100,
      render: (result) => getCheckResultIcon(result),
    },
    {
      title: 'Finding',
      dataIndex: 'actual_finding',
      key: 'actual_finding',
      width: 200,
      render: (finding) => finding || '-',
    },
    {
      title: 'Deviation',
      dataIndex: 'deviation',
      key: 'deviation',
      width: 200,
      render: (deviation) => (deviation ? <Tag color="orange">{deviation}</Tag> : '-'),
    },
  ];

  const certificateColumns: ColumnsType<any> = [
    {
      title: 'Type',
      dataIndex: 'certificate_type',
      key: 'certificate_type',
      width: 150,
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: 'Certificate No.',
      dataIndex: 'certificate_number',
      key: 'certificate_number',
      width: 150,
    },
    {
      title: 'Issuing Authority',
      dataIndex: 'issuing_authority',
      key: 'issuing_authority',
      width: 200,
    },
    {
      title: 'Issue Date',
      dataIndex: 'issue_date',
      key: 'issue_date',
      width: 120,
      render: (date) => (date ? dayjs(date).format('DD MMM YYYY') : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'verification_status',
      key: 'verification_status',
      width: 120,
      render: (status) => getStatusTag(status || 'Pending'),
    },
    {
      title: 'Heat/Batch',
      key: 'heat_batch',
      width: 150,
      render: (_, record) => {
        if (record.material_heat_number) return `Heat: ${record.material_heat_number}`;
        if (record.material_batch_number) return `Batch: ${record.material_batch_number}`;
        return '-';
      },
    },
  ];

  const mtcColumns: ColumnsType<MTCRecord> = [
    {
      title: 'MTC Number',
      dataIndex: 'mtc_number',
      key: 'mtc_number',
      width: 150,
    },
    {
      title: 'Type',
      dataIndex: 'certificate_type',
      key: 'certificate_type',
      width: 100,
      render: (type) => <Tag color="purple">{type}</Tag>,
    },
    {
      title: 'Heat Number',
      dataIndex: 'heat_number',
      key: 'heat_number',
      width: 120,
    },
    {
      title: 'Material Grade',
      dataIndex: 'material_grade',
      key: 'material_grade',
      width: 150,
    },
    {
      title: 'Tensile Strength',
      dataIndex: 'tensile_strength',
      key: 'tensile_strength',
      width: 120,
      render: (value) => (value ? `${value} MPa` : '-'),
    },
    {
      title: 'Yield Strength',
      dataIndex: 'yield_strength',
      key: 'yield_strength',
      width: 120,
      render: (value) => (value ? `${value} MPa` : '-'),
    },
    {
      title: 'Elongation',
      dataIndex: 'elongation',
      key: 'elongation',
      width: 100,
      render: (value) => (value ? `${value}%` : '-'),
    },
  ];

  const calculateChecklistProgress = () => {
    if (!checklistItems.length) return 0;
    const completed = checklistItems.filter(item => 
      item.check_result === 'Pass' || item.check_result === 'Fail' || item.check_result === 'N/A'
    ).length;
    return (completed / checklistItems.length) * 100;
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>Receiving Verification</h1>
          <p style={{ color: '#8c8c8c' }}>
            Verify received materials with checklist, certificates, and MTC tracking
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
          Create Verification
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={verifications}
          rowKey="id"
          loading={loading}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </Card>

      {/* Create Verification Modal */}
      <Modal
        title="Create Receiving Verification"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
        width={600}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateVerification}>
          <Form.Item
            name="grn_id"
            label="GRN"
            rules={[{ required: true, message: 'Please select GRN' }]}
          >
            <Select placeholder="Select GRN">
              {/* GRNs will be loaded dynamically */}
            </Select>
          </Form.Item>

          <Form.Item name="po_id" label="Purchase Order">
            <Select placeholder="Select PO">
              {/* POs will be loaded dynamically */}
            </Select>
          </Form.Item>

          <Form.Item
            name="supplier_id"
            label="Supplier"
            rules={[{ required: true, message: 'Please select supplier' }]}
          >
            <Select placeholder="Select Supplier">
              {/* Suppliers will be loaded dynamically */}
            </Select>
          </Form.Item>

          <Form.Item
            name="verification_date"
            label="Verification Date"
            rules={[{ required: true, message: 'Please select date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="verification_type"
            label="Verification Type"
            rules={[{ required: true, message: 'Please select type' }]}
          >
            <Select>
              <Option value="Document-Check">Document Check</Option>
              <Option value="Physical-Inspection">Physical Inspection</Option>
              <Option value="Both">Both</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Verification Details Modal */}
      <Modal
        title={`Verification Details: ${selectedVerification?.verification_number || ''}`}
        open={detailsModalVisible}
        onCancel={() => {
          setDetailsModalVisible(false);
          setSelectedVerification(null);
          setCurrentStep(0);
        }}
        width={1200}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>,
          selectedVerification?.status === 'Pending' && (
            <Button
              key="complete"
              type="primary"
              onClick={handleCompleteVerification}
              disabled={calculateChecklistProgress() < 100}
            >
              Complete Verification
            </Button>
          ),
        ]}
      >
        {selectedVerification && (
          <>
            <Alert
              message="Verification Progress"
              description={
                <Progress
                  percent={calculateChecklistProgress()}
                  status={calculateChecklistProgress() === 100 ? 'success' : 'active'}
                />
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="GRN Number">{selectedVerification.grn_number}</Descriptions.Item>
              <Descriptions.Item label="Supplier">{selectedVerification.supplier_name}</Descriptions.Item>
              <Descriptions.Item label="Status">{getStatusTag(selectedVerification.status)}</Descriptions.Item>
              <Descriptions.Item label="Result">
                {selectedVerification.overall_result ? getResultTag(selectedVerification.overall_result) : '-'}
              </Descriptions.Item>
            </Descriptions>

            {selectedVerification.grn_id ? (
              <ProcurementAttachmentsCard
                entityType={DOCUMENT_ENTITY_TYPE_GOODS_RECEIPT_NOTE}
                entityId={selectedVerification.grn_id}
                queryKeyPrefix={['documents', 'grn-receiving-verification']}
                title="GRN attachments"
              />
            ) : null}

            <Tabs defaultActiveKey="1">
              <TabPane tab={<span><FileTextOutlined /> Checklist ({checklistItems.length})</span>} key="1">
                <Table
                  columns={checklistColumns}
                  dataSource={checklistItems}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
                
                {selectedVerification.status === 'Pending' && (
                  <Card title="Update Checklist Item" style={{ marginTop: 16 }}>
                    <Form form={checklistForm} layout="vertical" onFinish={handleSubmitChecklistItem}>
                      <Row gutter={16}>
                        <Col span={6}>
                          <Form.Item name="item_category" label="Category">
                            <Select>
                              <Option value="Documents">Documents</Option>
                              <Option value="Certificates">Certificates</Option>
                              <Option value="Physical">Physical</Option>
                              <Option value="Markings">Markings</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="item_description" label="Description">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item name="check_result" label="Result">
                            <Select>
                              <Option value="Pass">Pass</Option>
                              <Option value="Fail">Fail</Option>
                              <Option value="N/A">N/A</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="actual_finding" label="Actual Finding">
                            <TextArea rows={2} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="deviation" label="Deviation">
                            <TextArea rows={2} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item name="is_mandatory" valuePropName="checked">
                        <Checkbox>Mandatory Check</Checkbox>
                      </Form.Item>
                      <Button type="primary" htmlType="submit">
                        Submit Checklist Item
                      </Button>
                    </Form>
                  </Card>
                )}
              </TabPane>

              <TabPane tab={<span><SafetyCertificateOutlined /> Certificates ({certificates.length})</span>} key="2">
                <Table
                  columns={certificateColumns}
                  dataSource={certificates}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
                
                {selectedVerification.status === 'Pending' && (
                  <Card title="Submit Certificate" style={{ marginTop: 16 }}>
                    <Form form={certificateForm} layout="vertical" onFinish={handleSubmitCertificate}>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item name="certificate_type" label="Type">
                            <Select>
                              <Option value="MTC">MTC</Option>
                              <Option value="CoC">CoC</Option>
                              <Option value="CoA">CoA</Option>
                              <Option value="Inspection-Cert">Inspection Certificate</Option>
                              <Option value="Calibration-Cert">Calibration Certificate</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="certificate_number" label="Certificate No.">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="issuing_authority" label="Issuing Authority">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item name="issue_date" label="Issue Date">
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="material_heat_number" label="Heat Number">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="material_batch_number" label="Batch Number">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item name="document_url" label="Document URL">
                        <Input placeholder="URL to certificate document" />
                      </Form.Item>
                      <Button type="primary" htmlType="submit">
                        Submit Certificate
                      </Button>
                    </Form>
                  </Card>
                )}
              </TabPane>

              <TabPane tab={<span><InboxOutlined /> MTC Records ({mtcRecords.length})</span>} key="3">
                <Table
                  columns={mtcColumns}
                  dataSource={mtcRecords}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={{ x: 1000 }}
                />
                
                {selectedVerification.status === 'Pending' && (
                  <Card title="Create MTC Record" style={{ marginTop: 16 }}>
                    <Form form={mtcForm} layout="vertical" onFinish={handleSubmitMTC}>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item name="mtc_number" label="MTC Number" rules={[{ required: true }]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="certificate_type" label="Type" rules={[{ required: true }]}>
                            <Select>
                              <Option value="MTC-3.1">MTC 3.1</Option>
                              <Option value="MTC-3.2">MTC 3.2</Option>
                              <Option value="Mill-Test">Mill Test</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="issue_date" label="Issue Date" rules={[{ required: true }]}>
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item name="heat_number" label="Heat Number">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="batch_number" label="Batch Number">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="issuing_mill" label="Issuing Mill">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="material_grade" label="Material Grade">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="material_specification" label="Specification">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item name="tensile_strength" label="Tensile Strength (MPa)">
                            <InputNumber style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="yield_strength" label="Yield Strength (MPa)">
                            <InputNumber style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="elongation" label="Elongation (%)">
                            <InputNumber style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item name="document_url" label="Document URL" rules={[{ required: true }]}>
                        <Input placeholder="URL to MTC document" />
                      </Form.Item>
                      <Button type="primary" htmlType="submit">
                        Create MTC Record
                      </Button>
                    </Form>
                  </Card>
                )}
              </TabPane>
            </Tabs>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ReceivingVerificationPage;
